import { useContext, useEffect, useMemo, useState } from 'react'
import { MenuTitles } from 'constants/menu'
import { AgencyContext } from 'contexts'
import { ContentLayout, PageLayout } from 'layout'
import {
  AgxDatePicker,
  AgxDropdown,
  AgxEmptyState,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  SAVE_BUTTON_STATES,
  useToast,
} from '@urbanx/agx.ui-components2'
import { AgxTextInput as AgxLegacyTextInput } from '@urbanx/agx-ui-components'
import {
  LicenceDetails,
  States,
  UrbanXDetailsModel,
  useGetUrbanXDetails,
  useInvalidateUseGetUrbanXDetails,
  useUpdateUrbanXDetails,
} from '@urbanx/agx.agencies.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { isDirtyEqual } from 'utils/compares'
import { FormPrompt } from 'components/FormPrompt'

const stateOptions = [
  { value: States.QLD, label: States.QLD },
  { value: States.NSW, label: States.NSW },
]

const StaffLicense = () => {
  const { userAccount } = useContext(AuthContext)
  const [selectedAgency] = useContext(AgencyContext)
  const { pushToast } = useToast()
  const [urbanXDetailsForm, setUrbanXDetailsForm] = useState<
    UrbanXDetailsModel | undefined
  >()
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const {
    data: urbanXDetails,
    isPending: urbanXDetailsPending,
    isError: urbanXDetailsError,
  } = useGetUrbanXDetails()
  const invalidateGetUrbanXDetails = useInvalidateUseGetUrbanXDetails()

  const { updateUrbanXDetails, updateUrbanXDetailsPending } =
    useUpdateUrbanXDetails({
      onSuccess: async (_, inputData) => {
        await invalidateGetUrbanXDetails()
        setUrbanXDetailsForm(inputData)
        setSaveButtonState(SAVE_BUTTON_STATES.SAVED)
        pushToast({
          type: 'success',
          message: `${MenuTitles.LICENSEE_DETAILS} updated successfully`,
        })
      },
      onError: () => {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        pushToast({
          type: 'error',
          message: `Error updating ${MenuTitles.LICENSEE_DETAILS}`,
        })
      },
    })

  useEffect(() => {
    if (urbanXDetailsPending) return

    setUrbanXDetailsForm(urbanXDetails)
  }, [urbanXDetails, urbanXDetailsPending])

  const onSaveChanges = async () => {
    if (!urbanXDetailsForm) return

    setSaveButtonState(SAVE_BUTTON_STATES.SAVING)

    try {
      await updateUrbanXDetails(urbanXDetailsForm)
    } catch {
      // onError is handled by the generated hook.
    }
  }

  const isFormDirty = useMemo(() => {
    if (!urbanXDetailsForm || !urbanXDetails) return false

    return !isDirtyEqual(urbanXDetailsForm, urbanXDetails)
  }, [urbanXDetailsForm, urbanXDetails])

  const saveChangesState = useMemo(() => {
    if (updateUrbanXDetailsPending) return SAVE_BUTTON_STATES.SAVING
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR)
      return SAVE_BUTTON_STATES.ERROR
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED
    return saveButtonState
  }, [isFormDirty, saveButtonState, updateUrbanXDetailsPending])

  const companyDetails = urbanXDetailsForm?.companyDetails
  const address = urbanXDetailsForm?.address
  const licenceDetails = companyDetails?.licences?.[0]

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.LICENSEE_DETAILS}
      isStaff
    >
      <ContentLayout
        hasSecondaryMenu
        secondaryActiveMenu={MenuTitles.LICENSEE_DETAILS}
        isStaff
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        {urbanXDetailsPending || urbanXDetailsError ? (
          <AgxEmptyState
            variant="minimal"
            label={
              urbanXDetailsPending
                ? 'Loading'
                : 'Unable to load licensee details'
            }
          />
        ) : (
          <AgxSpacer direction="vertical" gap="md" fill>
            <AgxSpacer
              direction="horizontal"
              fill
              className="borderBottomContainer"
            >
              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="start"
                contentAlignment="center"
                gap="sm"
              >
                <AgxTypography
                  id="staff-license-urbanx-details"
                  type="label"
                  size="large"
                >
                  Licensee Details
                </AgxTypography>
              </AgxSpacer>

              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="end"
                contentAlignment="center"
                gap="sm"
              >
                <AgxSaveChangesButton
                  id="btnSaveChanges"
                  text="Save Changes"
                  variant="primary"
                  size="medium"
                  state={saveChangesState}
                  onClick={() => {
                    void onSaveChanges()
                  }}
                  disabled={updateUrbanXDetailsPending || !isFormDirty}
                />
              </AgxSpacer>
            </AgxSpacer>

            <AgxSpacer
              direction="vertical"
              gap="md"
              className="container50Percent"
            >
              <AgxSpacer direction="vertical" gap="md">
                <AgxSpacer className="borderBottomContainer">
                  <AgxTypography
                    id="staff-license-company-details"
                    type="title"
                    size="large"
                  >
                    Company details
                  </AgxTypography>
                </AgxSpacer>

                <AgxSpacer direction="vertical" gap="md">
                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="tradingName"
                      label="Trading Name"
                      type="text"
                      stretch
                      defaultValue={urbanXDetailsForm?.tradingName}
                      onChange={(value) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          tradingName: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="licenseeName"
                      label="Licensee name"
                      type="text"
                      defaultValue={companyDetails?.licenseeName}
                      stretch
                      onChange={(value) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev?.companyDetails,
                            licenseeName: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxLegacyTextInput
                      id="abn"
                      label="ABN"
                      abn
                      defaultValue={companyDetails?.abn}
                      stretch
                      onInputValueChange={({ value }) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev?.companyDetails,
                            abn: value,
                          },
                        }))
                      }
                      noOptionalLabel
                    />

                    <AgxLegacyTextInput
                      id="acn"
                      label="ACN"
                      acn
                      defaultValue={companyDetails?.acn}
                      stretch
                      onInputValueChange={({ value }) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev?.companyDetails,
                            acn: value,
                          },
                        }))
                      }
                      noOptionalLabel
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxTextInput
                      id="licenceNumber"
                      label="Licence Number"
                      type="text"
                      defaultValue={licenceDetails?.licenceNumber ?? ''}
                      stretch
                      onChange={(value) => {
                        const updatedLicence: LicenceDetails = {
                          ...licenceDetails,
                          licenceNumber: value,
                        }

                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev?.companyDetails,
                            licences: [updatedLicence],
                          },
                        }))
                      }}
                      optionalText=""
                    />

                    <AgxDatePicker
                      id="dtExpiryDate"
                      label="Expiry Date"
                      stretch
                      value={licenceDetails?.expiryDate ?? null}
                      onChange={(value) => {
                        const updatedLicence: LicenceDetails = {
                          ...licenceDetails,
                          expiryDate: value ?? undefined,
                        }

                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          companyDetails: {
                            ...prev?.companyDetails,
                            licences: [updatedLicence],
                          },
                        }))
                      }}
                      optionalText=""
                    />
                  </AgxSpacer>
                </AgxSpacer>
              </AgxSpacer>

              <AgxSpacer direction="vertical" gap="md">
                <AgxSpacer className="borderBottomContainer">
                  <AgxTypography
                    id="staff-license-contact-details"
                    type="title"
                    size="large"
                  >
                    Contact Details
                  </AgxTypography>
                </AgxSpacer>

                <AgxSpacer direction="vertical" gap="md">
                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxTextInput
                      id="listingsEmail"
                      label="Listings Email"
                      type="email"
                      defaultValue={urbanXDetailsForm?.listingsEmail}
                      stretch
                      onInputValueChange={({ value }) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          listingsEmail: value,
                        }))
                      }
                      optionalText=""
                    />

                    <AgxTextInput
                      id="contractEmail"
                      label="Contract Email"
                      type="email"
                      defaultValue={urbanXDetailsForm?.contractsEmail}
                      stretch
                      onInputValueChange={({ value }) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          contractsEmail: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="phoneNumber"
                      label="Listings Phone Number"
                      type="text"
                      defaultValue={urbanXDetailsForm?.listingsPhoneNumber}
                      stretch
                      onChange={(value) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          listingsPhoneNumber: value,
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>
                </AgxSpacer>
              </AgxSpacer>

              <AgxSpacer direction="vertical" gap="md">
                <AgxSpacer className="borderBottomContainer">
                  <AgxTypography
                    id="staff-license-address"
                    type="title"
                    size="large"
                  >
                    URBAN X Address
                  </AgxTypography>
                </AgxSpacer>

                <AgxSpacer direction="vertical" gap="md">
                  <AgxSpacer direction="horizontal">
                    <AgxTextInput
                      id="streetAddress"
                      label="Street Address"
                      type="text"
                      defaultValue={address?.streetAddress}
                      stretch
                      onChange={(value) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          address: {
                            ...prev?.address,
                            streetAddress: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>

                  <AgxSpacer direction="horizontal" gap="sm">
                    <AgxTextInput
                      id="suburb"
                      label="Suburb"
                      type="text"
                      defaultValue={address?.suburb}
                      stretch
                      onChange={(value) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          address: {
                            ...prev?.address,
                            suburb: value,
                          },
                        }))
                      }
                      optionalText=""
                    />

                    <AgxDropdown
                      id="state"
                      label="State"
                      hideOptionalText
                      selected={address?.state}
                      onChange={(value) => {
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          address: {
                            ...prev?.address,
                            state: value,
                          },
                        }))
                      }}
                      options={stateOptions}
                    />

                    <AgxTextInput
                      id="postcode"
                      label="Postcode"
                      type="text"
                      defaultValue={address?.postalCode}
                      stretch
                      onChange={(value) =>
                        setUrbanXDetailsForm((prev) => ({
                          ...prev,
                          address: {
                            ...prev?.address,
                            postalCode: value,
                          },
                        }))
                      }
                      optionalText=""
                    />
                  </AgxSpacer>
                </AgxSpacer>
              </AgxSpacer>
            </AgxSpacer>
          </AgxSpacer>
        )}
      </ContentLayout>
    </PageLayout>
  )
}

export default StaffLicense
